import { useNavigate } from "@tanstack/react-location"
import { queryClient } from "bonzai"
import { custom } from "../../../bonzai/bonzai"
import type { NotificationData, NotificationType } from "../NotificationItem"
import { NotificationMenu } from "./NotificationMenu"

export const NotificationMenuConnected = () => {
  const navigate = useNavigate()
  const { data, error, isLoading, fetchNextPage, isFetchingNextPage } =
    custom.getNotifications.useInfiniteQuery([], {
      throwOnError: false,
      refetchInterval: 60_000,
      initialPageParam: [{ "page[number]": 1 }] as const,
      getNextPageParam: (lastPage) => {
        const { current_page, total_pages } = lastPage.meta.pagination
        if (current_page >= total_pages) return undefined
        return [{ "page[number]": current_page + 1 }] as const
      },
    })

  const markNotificationsAsReadMutation =
    custom.markNotificationsAsRead.useMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey.includes("getNotifications"),
        })
      },
    })

  const notifications: NotificationData[] =
    data?.pages.flatMap((page) =>
      page.data.map(
        (n) =>
          ({
            id: n.id,
            notification_type: n.notification_type as NotificationType,
            read_at: n.read_at,
            data: n.data,
            created_at: n.created_at,
            is_read: n.is_read,
          } as NotificationData)
      )
    ) ?? []

  const unreadCount = data?.pages[0]?.meta.unread_count ?? 0

  const handleNotificationRead = (id: string) => {
    markNotificationsAsReadMutation.mutate([{ notification_ids: [Number(id)] }])
  }

  const handleMarkAllAsRead = () => {
    markNotificationsAsReadMutation.mutate([{ mark_all: true }])
  }

  const handleNotificationClick = (notification: NotificationData) => {
    // path is backend-supplied and router-relative (e.g. /for-you/product/123);
    // it may be null when there's nothing to link to, so only navigate when present.
    if (notification.data.path) {
      navigate({ to: notification.data.path })
    }
  }

  return (
    <NotificationMenu
      notifications={notifications}
      unreadCount={unreadCount}
      onNotificationRead={handleNotificationRead}
      onMarkAllAsRead={handleMarkAllAsRead}
      onNotificationClick={handleNotificationClick}
      onLoadMore={fetchNextPage}
      isLoadingMore={isFetchingNextPage}
      skeletonCount={5}
      error={error?.message}
      isLoading={isLoading}
    />
  )
}
